import React, { memo } from 'react';
import { Phone, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Avatar } from './Avatar';

export const MessageItem = memo(function MessageItem({
  message,
  isOwn,
  isDeleted,
  isDeletedByMe,
  isCall,
  isSelected,
  isDark,
  isGroup,
  onMessageClick,
  onDeleteClick,
  formatTime
}) {
  // Ne pas afficher les messages supprimés par l'utilisateur
  if (isDeletedByMe) return null;
  
  // Message d'appel (centré)
  if (isCall) {
    return (
      <div className="flex justify-center my-4">
        <div className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-full text-sm',
          isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-600'
        )}>
          <Phone size={16} className="text-green-500 flex-shrink-0" />
          <span>
            {message.sender_prenom} - Appel vocal
            {message.call_duration && ` (${Math.floor(message.call_duration / 60)}:${(message.call_duration % 60).toString().padStart(2, '0')})`}
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className={cn(
        'flex gap-2 group relative w-full',
        isOwn ? 'justify-end' : 'justify-start'
      )}
    >
      {/* Avatar pour les messages reçus */}
      {!isOwn && (
        <div className="flex-shrink-0 self-end">
          <Avatar user={{ prenom: message.sender_prenom, nom: message.sender_nom }} size="sm" />
        </div>
      )}
      
      {/* Conteneur du message avec bouton de suppression */}
      <div className={cn(
        'flex items-center gap-2 max-w-[85%] sm:max-w-[75%] md:max-w-[70%]',
        isOwn ? 'flex-row' : 'flex-row-reverse'
      )}>
        {/* Bouton de suppression (uniquement pour ses propres messages) */}
        {isOwn && !isDeleted && (
          <button
            onClick={onDeleteClick}
            className={cn(
              'p-1.5 rounded-full transition-opacity duration-200 flex-shrink-0',
              isSelected ? 'opacity-100' : 'opacity-0 md:group-hover:opacity-100',
              isDark ? 'bg-zinc-700 hover:bg-red-600 text-zinc-300 hover:text-white' : 'bg-gray-300 hover:bg-red-500 text-gray-600 hover:text-white'
            )}
            title="Supprimer"
            data-testid={`delete-message-${message.id}`}
          >
            <X size={14} />
          </button>
        )}
        
        {/* Bulle du message */}
        <div 
          onClick={onMessageClick}
          className={cn(
            'rounded-2xl px-4 py-2 cursor-pointer min-w-[60px]',
            isDeleted 
              ? (isDark ? 'bg-zinc-800/50 text-zinc-500 italic' : 'bg-gray-100 text-gray-400 italic')
              : isOwn 
                ? 'bg-red-500 text-white rounded-br-sm' 
                : isDark 
                  ? 'bg-zinc-800 text-white rounded-bl-sm' 
                  : 'bg-white text-gray-900 rounded-bl-sm shadow-sm border border-gray-100'
          )}>
          {/* Nom de l'expéditeur dans les groupes */}
          {!isOwn && isGroup && !isDeleted && (
            <p className={cn(
              'text-xs font-medium mb-1',
              isDark ? 'text-zinc-400' : 'text-gray-500'
            )}>
              {message.sender_prenom}
            </p>
          )}
          {/* Contenu du message */}
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
          {/* Heure */}
          <p className={cn(
            'text-xs mt-1 text-right',
            isDeleted 
              ? (isDark ? 'text-zinc-600' : 'text-gray-400')
              : isOwn ? 'text-red-200' : isDark ? 'text-zinc-500' : 'text-gray-400'
          )}>
            {formatTime(message.created_at)}
          </p>
        </div>
      </div>
    </div>
  );
});
